/**
 * InteractionComponent - Manages interactable objects for a player pawn.
 * Interactables register themselves with this component when the player enters their range.
 * This component receives interaction input from the Pawn and delegates to the appropriate interactable.
 */

import * as THREE from 'three';

import { Actor } from '../../actors/Actor.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { SceneComponent } from '../SceneComponent.js';

import type { IInteractable } from './IInteractable.js';

@EngineClass('InteractionComponent')
export class InteractionComponent extends SceneComponent {
  /** Set of interactables currently registered with this component (using WeakRef to prevent memory leaks) */
  private registeredInteractables = new Set<WeakRef<IInteractable>>();

  /** The currently active interactable (interaction in progress) */
  private currentInteractable: WeakRef<IInteractable> | null = null;

  /**
   * Called by the Pawn when the interaction key is pressed.
   * Selects the best interactable from the registered set and begins interaction.
   * @returns True if an interaction was started, false otherwise
   */
  public beginInteract(): boolean {
    const player = this.getActor();
    if (!player) return false;

    // Clean up dead references before selecting
    this.cleanupDeadReferences();

    const target = this.selectInteractable(player);
    if (!target || !target.canInteract(player)) return false;

    this.currentInteractable = new WeakRef(target);
    return target.beginInteract(player);
  }

  /**
   * Called by the Pawn when the interaction key is released.
   * Ends interaction with the current interactable if one is active.
   */
  public endInteract(): void {
    const player = this.getActor();
    if (!player) return;

    const current = this.currentInteractable?.deref();
    if (!current) {
      this.currentInteractable = null;
      return;
    }

    if (current.endInteract) {
      current.endInteract(player);
    }
    this.currentInteractable = null;
  }

  /**
   * Registers an interactable with this component.
   * Called by interactables when the player enters their interaction range.
   * @param interactable - The interactable to register
   */
  public register(interactable: IInteractable): void {
    this.registeredInteractables.add(new WeakRef(interactable));
  }

  /**
   * Unregisters an interactable from this component.
   * Called by interactables when the player leaves their interaction range.
   * @param interactable - The interactable to unregister
   */
  public unregister(interactable: IInteractable): void {
    // Find and remove the WeakRef that points to this interactable
    for (const ref of this.registeredInteractables) {
      if (ref.deref() === interactable) {
        this.registeredInteractables.delete(ref);
        break;
      }
    }

    // Clear current interactable if it was the one being unregistered
    const current = this.currentInteractable?.deref();
    if (current === interactable) {
      this.currentInteractable = null;
    }
  }

  /**
   * Returns the currently active interactable (if interaction is in progress).
   * @returns The current interactable or null
   */
  public getCurrentInteractable(): IInteractable | null {
    return this.currentInteractable?.deref() ?? null;
  }

  /**
   * Returns all currently registered interactables.
   * Automatically cleans up dead references.
   * @returns Array of registered interactables
   */
  public getRegisteredInteractables(): IInteractable[] {
    this.cleanupDeadReferences();

    const result: IInteractable[] = [];
    for (const ref of this.registeredInteractables) {
      const interactable = ref.deref();
      if (interactable) {
        result.push(interactable);
      }
    }
    return result;
  }

  /**
   * Returns the best interactable for the player to interact with.
   * Filters by canInteract, sorts by priority (higher first), then by distance (closer first).
   * @param player - The player actor
   * @returns The best interactable or null if none available
   */
  private selectInteractable(player: Actor): IInteractable | null {
    const valid: IInteractable[] = [];

    for (const ref of this.registeredInteractables) {
      const interactable = ref.deref();
      if (interactable && interactable.canInteract(player)) {
        valid.push(interactable);
      }
    }

    if (valid.length === 0) return null;

    const playerPos = this.getWorldPosition(new THREE.Vector3());

    return valid.sort((a, b) => {
      // Higher priority first
      const priorityDiff = b.getInteractionPriority() - a.getInteractionPriority();
      if (priorityDiff !== 0) return priorityDiff;

      // Closer distance first (tiebreaker)
      const distA = a.getWorldPosition().distanceTo(playerPos);
      const distB = b.getWorldPosition().distanceTo(playerPos);
      return distA - distB;
    })[0];
  }

  /**
   * Returns the interaction prompt for the best available interactable.
   * Useful for UI display.
   * @returns The interaction prompt or empty string if none available
   */
  public getCurrentPrompt(): string {
    const player = this.getActor();
    if (!player) return '';

    const target = this.selectInteractable(player);
    return target?.getInteractionPrompt() ?? '';
  }

  /**
   * Checks if there is an interactable available for interaction.
   * @returns True if at least one interactable can be interacted with
   */
  public hasAvailableInteractable(): boolean {
    const player = this.getActor();
    if (!player) return false;

    for (const ref of this.registeredInteractables) {
      const interactable = ref.deref();
      if (interactable && interactable.canInteract(player)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Cleans up dead WeakRefs from the registered set.
   * Called automatically when iterating over interactables.
   */
  private cleanupDeadReferences(): void {
    for (const ref of this.registeredInteractables) {
      if (!ref.deref()) {
        this.registeredInteractables.delete(ref);
      }
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Interaction';
  }
}
